/**
 * Sincronização do catálogo do ERP — fluxo de 3 passos:
 *
 *   1. Importar categorias do ERP (cria/atualiza rotina __discover-categorias,
 *      executa, popula product_categories).
 *   2. Usuário marca quais categorias quer exibir nas TVs (sync_enabled).
 *   3. Gerar as rotinas de produto escopadas (snapshot full + novos via keyset
 *      na PK) com WHERE p.grupo IN (selecionadas).
 *
 * Foi pensado pro Visual ERP (MySQL) mas o template engine no backend é
 * trocável por outros ERPs no futuro.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, RefreshCw, CheckCircle2, AlertCircle, Loader2, Sparkles, Database, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { toast } from 'sonner';
import { timeAgo } from '@/utils/cn';

interface ErpCategory {
  nome: string;
  codigo_externo: string | null;
  departamento_codigo: string | null;
  departamento: string | null;
  qtd_produtos_erp: number | null;
  sync_enabled: boolean;
  last_seen_in_erp_at: string | null;
  origem: 'erp' | 'manual';
}

interface DepartamentoGroup {
  departamento: string;
  departamento_codigo: string | null;
  qtd_total_produtos: number;
  qtd_categorias: number;
  categorias: ErpCategory[];
}

interface ErpCategoriesResponse {
  departamentos: DepartamentoGroup[];
  total_categorias: number;
  total_sync_enabled: number;
}

interface Connection {
  id: number;
  agent_id: string;
  name: string;
  db_type: string;
}

interface AgentMachine {
  id: string;
  label: string;
  status: string;
}

export function CatalogSyncPage() {
  const qc = useQueryClient();

  const { data: connections = [] } = useQuery({
    queryKey: ['agent-etl', 'connections'],
    queryFn: () => apiClient.agentEtl.listConnections().then(r => r.data.connections as Connection[]),
  });

  const { data: agents = [] } = useQuery({
    queryKey: ['agent-etl', 'agents'],
    queryFn: () => apiClient.agentEtl.listAgents().then(r => r.data.agents as AgentMachine[]),
    enabled: true,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['agent-etl', 'erp-categories'],
    queryFn: () => apiClient.agentEtl.listErpCategories().then(r => r.data as ErpCategoriesResponse),
  });

  const [showCreateConn, setShowCreateConn] = useState(false);
  const [newConnName, setNewConnName] = useState('ERP');
  const [newConnDbType, setNewConnDbType] = useState('mysql');
  const [newConnAgentId, setNewConnAgentId] = useState('');
  const [createdConnId, setCreatedConnId] = useState<number | null>(null);

  const [connId, setConnId] = useState<number | null>(null);
  // Quando uma única connection existir, seleciona automaticamente.
  const effectiveConnId = connId ?? (connections.length === 1 ? connections[0].id : null);

  // Seleção local mutável — só salva no servidor ao clicar em "Salvar seleção".
  // Inicializa quando os dados chegam, mas preserva edição em andamento.
  const [selection, setSelection] = useState<Set<string> | null>(null);
  const currentSelection = useMemo(() => {
    if (selection) return selection;
    if (!data) return new Set<string>();
    return new Set(
      data.departamentos.flatMap(d =>
        d.categorias.filter(c => c.sync_enabled).map(c => c.codigo_externo ?? `nome:${c.nome}`)
      )
    );
  }, [selection, data]);

  function toggle(key: string) {
    const next = new Set(currentSelection);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelection(next);
  }
  function toggleDepto(grp: DepartamentoGroup) {
    const keys = grp.categorias.map(c => c.codigo_externo ?? `nome:${c.nome}`);
    const allOn = keys.every(k => currentSelection.has(k));
    const next = new Set(currentSelection);
    if (allOn) keys.forEach(k => next.delete(k));
    else       keys.forEach(k => next.add(k));
    setSelection(next);
  }

  // ---------- Mutations ----------
  const createConnMutation = useMutation({
    mutationFn: async () => {
      const agentId = newConnAgentId || (agents.length === 1 ? agents[0].id : '');
      if (!agentId) throw new Error('Selecione o agente');
      if (!newConnName.trim()) throw new Error('Informe um nome para a conexão');
      const { data } = await apiClient.agentEtl.createConnection({
        agent_id: agentId,
        name: newConnName.trim(),
        db_type: newConnDbType,
      });
      return data as { id: number };
    },
    onSuccess: (data) => {
      setCreatedConnId(data.id);
      setShowCreateConn(false);
      qc.invalidateQueries({ queryKey: ['agent-etl', 'connections'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || e.message || 'Falha ao criar conexão'),
  });

  const discoverMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveConnId) throw new Error('Selecione uma conexão');
      // 1. Cria/atualiza só a rotina __discover-categorias na connection.
      const { data: seed } = await apiClient.agentEtl.seedVisualErpRoutines(effectiveConnId, true);
      // 2. Trigger pra rodar no próximo poll do agente.
      const routinesResp = await apiClient.agentEtl.listRoutines(effectiveConnId);
      const discover = (routinesResp.data.routines as { id: number; name: string }[])
        .find(r => r.name === '__discover-categorias');
      if (discover) await apiClient.agentEtl.triggerRoutine(discover.id);
      return seed;
    },
    onSuccess: () => {
      toast.success('Rotina de descoberta enfileirada — categorias aparecem aqui após o próximo poll do agente.');
      qc.invalidateQueries({ queryKey: ['agent-etl', 'erp-categories'] });
      qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || e.message || 'Falha ao iniciar descoberta'),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const codigos: string[] = [];
      const nomes: string[] = [];
      currentSelection.forEach(k => {
        if (k.startsWith('nome:')) nomes.push(k.slice(5));
        else codigos.push(k);
      });
      return apiClient.agentEtl.saveCategorySelection({
        codigos_externos: codigos,
        nomes,
      });
    },
    onSuccess: () => {
      toast.success('Seleção salva.');
      setSelection(null);
      qc.invalidateQueries({ queryKey: ['agent-etl', 'erp-categories'] });
    },
    onError: () => toast.error('Falha ao salvar seleção.'),
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveConnId) throw new Error('Selecione uma conexão');
      return apiClient.agentEtl.seedVisualErpRoutines(effectiveConnId, false);
    },
    onSuccess: (resp) => {
      const r = resp.data as { created: number; updated: number; categorias_no_escopo: number };
      toast.success(
        `${r.created} criada(s) · ${r.updated} atualizada(s) · escopo: ${r.categorias_no_escopo} categorias.`
      );
      qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Falha ao gerar rotinas.'),
  });

  // ---------- States ----------
  const hasErpCategories = (data?.departamentos.some(d =>
    d.categorias.some(c => c.origem === 'erp')
  )) ?? false;
  const totalSelected = currentSelection.size;
  const hasUnsavedChanges = selection !== null;
  const lastErpSync = data?.departamentos.flatMap(d => d.categorias)
    .map(c => c.last_seen_in_erp_at)
    .filter((x): x is string => !!x)
    .sort()
    .pop();

  return (
    <div className="container-app py-8 space-y-6">
      <div>
        <Link to="/settings/agent" className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Voltar para agentes
        </Link>
        <h1 className="text-2xl font-semibold">Sincronização do catálogo</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Descubra as categorias do seu ERP, marque o que deve aparecer nas TVs e gere as
          rotinas de sincronização escopadas.
        </p>
      </div>

      {!connections.length && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p>Nenhuma conexão de banco cadastrada ainda.</p>
              <p className="text-xs mt-0.5 text-amber-700">
                Crie a conexão aqui para gerar o ID, depois abra o app do agente e use esse ID em{' '}
                <strong>Cadastrar ERP</strong>.
              </p>
            </div>
            {!showCreateConn && !createdConnId && (
              <Button size="sm" onClick={() => setShowCreateConn(true)}>
                <Plus className="w-3 h-3 mr-1" /> Criar conexão
              </Button>
            )}
          </div>

          {createdConnId && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 text-xs space-y-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span className="font-semibold">Conexão criada com sucesso!</span>
              </div>
              <p>
                Use o <strong>Connection ID: {createdConnId}</strong> no campo "Connection ID" do app do agente
                ao clicar em <strong>Cadastrar ERP</strong>.
              </p>
            </div>
          )}

          {showCreateConn && (
            <div className="rounded-md border border-amber-300 bg-white p-4 space-y-3 text-zinc-800">
              <p className="text-xs font-medium text-zinc-600">Nova conexão com ERP</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {agents.length > 1 && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium">Agente</label>
                    <select
                      className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
                      value={newConnAgentId}
                      onChange={(e) => setNewConnAgentId(e.target.value)}
                    >
                      <option value="">— selecione —</option>
                      {agents.map(a => (
                        <option key={a.id} value={a.id}>{a.label || a.id.slice(0, 8)}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-xs font-medium">Nome</label>
                  <input
                    type="text"
                    className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
                    value={newConnName}
                    onChange={(e) => setNewConnName(e.target.value)}
                    placeholder="ex: ERP"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium">Tipo de banco</label>
                  <select
                    className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm"
                    value={newConnDbType}
                    onChange={(e) => setNewConnDbType(e.target.value)}
                  >
                    <option value="mysql">MySQL</option>
                    <option value="sqlserver">SQL Server</option>
                    <option value="postgres">PostgreSQL</option>
                    <option value="firebird">Firebird</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowCreateConn(false)}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={() => createConnMutation.mutate()}
                  disabled={createConnMutation.isPending}
                >
                  {createConnMutation.isPending
                    ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    : <Plus className="w-3 h-3 mr-1" />}
                  Criar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {connections.length > 1 && (
        <div>
          <label className="text-sm font-medium">Conexão</label>
          <select
            className="mt-1 w-full md:w-96 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            value={effectiveConnId ?? ''}
            onChange={(e) => setConnId(Number(e.target.value) || null)}
          >
            <option value="">— selecione —</option>
            {connections.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.db_type})</option>
            ))}
          </select>
        </div>
      )}

      {/* ---------- Passo 1: Discovery ---------- */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">1</span>
              <h2 className="font-semibold">Importar categorias do ERP</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Cria/atualiza a rotina <code>__discover-categorias</code> e dispara execução.
              O agente lê <code>secoes</code> e <code>grupos</code> do seu ERP (Visual) e popula a lista abaixo.
            </p>
            {lastErpSync && (
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                Última descoberta: {timeAgo(lastErpSync)}
              </div>
            )}
          </div>
          <Button
            onClick={() => discoverMutation.mutate()}
            disabled={!effectiveConnId || discoverMutation.isPending}
          >
            {discoverMutation.isPending
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : <Download className="w-4 h-4 mr-1" />}
            {hasErpCategories ? 'Atualizar categorias' : 'Importar categorias'}
          </Button>
        </div>
      </section>

      {/* ---------- Passo 2: Seleção ---------- */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">2</span>
              <h2 className="font-semibold">Marcar categorias para sincronizar</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Apenas as categorias marcadas entram no <code>WHERE g.codigo IN (...)</code> das rotinas de produto.
              Pense: <em>"o que faz sentido aparecer numa TV no PDV?"</em>
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold">{totalSelected}</div>
            <div className="text-xs text-muted-foreground">selecionada{totalSelected === 1 ? '' : 's'}</div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        ) : !hasErpCategories ? (
          <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center text-sm text-muted-foreground">
            <Database className="w-6 h-6 mx-auto mb-2 text-zinc-400" />
            Nenhuma categoria importada do ERP ainda.
            <br />
            Clique em <strong>Importar categorias</strong> acima.
            <div className="mt-2">
              <button
                onClick={() => refetch()}
                className="text-xs text-violet-700 hover:underline inline-flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Recarregar (após próximo poll do agente)
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-2 max-h-[480px] overflow-y-auto pr-1">
            {data?.departamentos.map(grp => {
              const keys = grp.categorias.map(c => c.codigo_externo ?? `nome:${c.nome}`);
              const checkedCount = keys.filter(k => currentSelection.has(k)).length;
              const allOn = checkedCount === keys.length;
              const someOn = checkedCount > 0 && !allOn;

              return (
                <details key={grp.departamento + (grp.departamento_codigo ?? '')} className="rounded-md border border-zinc-200" open={someOn}>
                  <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-zinc-50 list-none">
                    <input
                      type="checkbox"
                      checked={allOn}
                      ref={el => { if (el) el.indeterminate = someOn; }}
                      onChange={() => toggleDepto(grp)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4"
                    />
                    <div className="flex-1 flex items-center gap-2">
                      <span className="font-medium">{grp.departamento}</span>
                      <Badge variant="outline" className="text-xs">
                        {grp.qtd_total_produtos.toLocaleString('pt-BR')} produtos
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {checkedCount}/{grp.qtd_categorias}
                      </Badge>
                    </div>
                  </summary>
                  <div className="border-t border-zinc-200 divide-y divide-zinc-100">
                    {grp.categorias.map(c => {
                      const key = c.codigo_externo ?? `nome:${c.nome}`;
                      return (
                        <label
                          key={key}
                          className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={currentSelection.has(key)}
                            onChange={() => toggle(key)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            <span className="truncate">{c.nome}</span>
                            {c.origem === 'manual' && (
                              <Badge variant="outline" className="text-xs">manual</Badge>
                            )}
                          </div>
                          {c.qtd_produtos_erp != null && (
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {c.qtd_produtos_erp.toLocaleString('pt-BR')}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
          {hasUnsavedChanges && (
            <Button variant="outline" onClick={() => setSelection(null)} disabled={saveMutation.isPending}>
              Descartar alterações
            </Button>
          )}
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!hasUnsavedChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Salvar seleção
          </Button>
        </div>
      </section>

      {/* ---------- Passo 3: Gerar rotinas ---------- */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">3</span>
              <h2 className="font-semibold">Gerar rotinas de sincronização</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Cria as rotinas do Visual ERP escopadas pelas categorias salvas. Se já existirem,
              regenera o <code>WHERE IN (...)</code> com a seleção atual.
            </p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 ml-1">
              <li>• <strong>produtos-snapshot</strong> — catálogo completo (preço, descrição, promoção) a cada 30min</li>
              <li>• <strong>produtos-novos</strong> — lançamentos via keyset na PK (<code>codigo &gt; cursor</code>), a cada 5min</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-1">
              Sem índice no ERP, um full scan a cada 30min é o jeito mais leve de captar
              preço/descrição que mudou. Estoque não é sincronizado.
            </p>
          </div>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={!effectiveConnId || hasUnsavedChanges || generateMutation.isPending}
            title={hasUnsavedChanges ? 'Salve a seleção primeiro' : ''}
          >
            {generateMutation.isPending
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : <Sparkles className="w-4 h-4 mr-1" />}
            Gerar / atualizar rotinas
          </Button>
        </div>
        {data && data.total_sync_enabled === 0 && (
          <div className="text-xs rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800">
            Nenhuma categoria marcada. As rotinas serão criadas <strong>sem escopo</strong> —
            o que pode trazer TODOS os produtos do ERP. Recomendado marcar primeiro.
          </div>
        )}
      </section>

      <div className="flex justify-between text-xs text-muted-foreground border-t border-zinc-100 pt-3">
        <Link to="/settings/agent/routines" className="hover:underline inline-flex items-center gap-1">
          → Ver rotinas existentes
        </Link>
        <Link to="/settings/agent/logs" className="hover:underline inline-flex items-center gap-1">
          → Logs de execução
        </Link>
      </div>
    </div>
  );
}
